import React, { useState, useRef, useEffect } from 'react';
import { message, Modal } from 'antd';
import { Share2, Download, Settings } from 'lucide-react';
import { QuotationViewerProps } from './Quotation';
import PopupComponent from '../../components/PopupComponent';
import styled from 'styled-components';
import { BUCKET_URL } from '../../config/config';
import toast from 'react-hot-toast';
import logger, { Logger } from '../../utils/logger';
import { convertQuotation, updateQuotations, updateQuotationStatus } from '../../services/quotationService';
import axios from 'axios';
import { API_URL } from '../../config/config';
import { getContrastTextColor, getStatusColor, getStatusDisplayName, isValidStatus } from './statusUtils';

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 70vh;
  min-height: 600px;
`;

const IframeContainer = styled.div`
  position: relative;
  background: white;
  border-radius: 8px;
  flex: 1;
  overflow: hidden;
`;

const StyledIframe = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
  border-radius: 8px;
  display: block;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
`;

const Button = styled.button<{ primary?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  border: ${props => props.primary ? 'none' : '1px solid #e0e0e0'};
  background: ${props => props.primary ? '#1677ff' : 'white'};
  color: ${props => props.primary ? 'white' : '#666'};
  
  &:hover {
    opacity: 0.9;
  }
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 24px;
`;

const ErrorMessage = styled.div`
  color: #ff4d4f;
  margin-bottom: 16px;
`;

const LoadingSpinner = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
`;

// Options Popup Menu Styles
const OptionsMenu = styled.div<{ visible: boolean }>`
  position: absolute;
  right: 0;
  bottom: 60px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 3px 6px rgba(0,0,0,0.16);
  min-width: 200px;
  z-index: 1050;
  display: ${props => props.visible ? 'block' : 'none'};
`;

const OptionsMenuSection = styled.div`
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
  
  &:last-child {
    border-bottom: none;
  }
`;

const OptionsMenuTitle = styled.div`
  color: #999;
  font-size: 12px;
  padding: 5px 16px;
`;

const OptionsMenuItem = styled.div<{ disabled?: boolean }>`
  padding: 8px 16px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.disabled ? 0.5 : 1};
  
  &:hover {
    background: ${props => props.disabled ? 'transparent' : '#f5f5f5'};
  }
`;

const QuotationViewer: React.FC<QuotationViewerProps> = ({
  visible,
  onClose,
  quotationId,
  token,
  userId,
  onEdit,
  order,
  bucketUrl,
  onQuotationUpdated
}) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [iframeError, setIframeError] = useState<boolean>(false);
  const [statusUpdateLoading, setStatusUpdateLoading] = useState<boolean>(false);
  const [conversionLoading, setConversionLoading] = useState<boolean>(false);
  const [optionsMenuVisible, setOptionsMenuVisible] = useState<boolean>(false);
  const optionsButtonRef = useRef<HTMLButtonElement>(null);

  const [localOrder, setLocalOrder] = useState(order);


  useEffect(() => {
    setLocalOrder(order);
  }, [order]);
  
  const getWebViewUrl = (): string => {
    const cleanToken = token ? token.replace('Bearer ', '') : '';
    return `https://integration.3sigmacrm.com/quotation/${quotationId}?token=${cleanToken}`;
  };

  const handleDownload = () => {
    if (!order?.quotationUrl) return;
    const fileUrl = (bucketUrl || BUCKET_URL) + order.quotationUrl;
    
    const form = document.createElement('form');
    form.method = 'GET';
    form.action = fileUrl;
    form.target = '_blank';
    document.body.appendChild(form);
    form.submit();
    document.body.removeChild(form);
  };

  const handleCopy = () => {
    try {
      if (order?.uniqueLinkId) {
        const shareUrl = `https://integration.3sigmacrm.com/share/quotation/${order.uniqueLinkId}`;
        navigator.clipboard.writeText(shareUrl);
        
        toast.success('Quotation link copied to clipboard', {
          style: {
            zIndex: 9999 // Higher than your modal's z-index
          }
        });
      }
    } catch (error) {
      console.error('Copy error:', error);
    }
  };

  const handleIframeLoad = (e: React.SyntheticEvent<HTMLIFrameElement>): void => {
    setLoading(false);
    const iframe = e.target as HTMLIFrameElement;
    if (iframe.contentWindow) {
      try {
        const doc = iframe.contentWindow.document;
        doc.body.style.margin = '0';
        doc.body.style.padding = '0';
        doc.body.style.height = '100%';
        doc.body.style.overflowX = 'hidden';
        doc.documentElement.style.height = '100%';
        doc.documentElement.style.overflowX = 'hidden';
      } catch (error) {
        Logger.log('Could not modify iframe content');
      }
    }
  };


  const handleStatusUpdate = async (newStatus: string) => {
    logger.log(`StatusUpdate: Starting update to ${newStatus}`);
    
    if (!localOrder?._id) {
        console.error('StatusUpdate: No order ID available');
        return;
    }
    
    setStatusUpdateLoading(true);
    try {
        logger.log(`StatusUpdate: Sending update request for ID ${localOrder._id} to status ${newStatus}`);
        
        // Use the dedicated service function
        await updateQuotationStatus(localOrder._id, newStatus);
        
        logger.log(`StatusUpdate: Status updated successfully`);
        message.success(`Status updated to ${getStatusDisplayName(newStatus)}`);
        
        // Create a new order object with updated status
        const updatedOrder = {
            ...localOrder,
            status: newStatus
        };
        
        // Update the local state
        setLocalOrder(updatedOrder);
        
        // Notify parent component to refresh the grid
        if (onQuotationUpdated) {
            onQuotationUpdated(updatedOrder);
        }
        
        // Close the options menu
        setOptionsMenuVisible(false);
    } catch (error) {
        console.error('StatusUpdate: Error during update:', error);
        message.error('Failed to update status');
    } finally {
        setStatusUpdateLoading(false);
    }
};

  const getStatusPreferences = () => {
    logger.log('Getting status preferences from localStorage directly');
    try {
      const userPrefs = localStorage.getItem("user_preferences");
      logger.log('Raw user preferences from localStorage:', userPrefs);
      
      if (!userPrefs) {
        logger.log('No user preferences found in localStorage');
        return null;
      }
      
      const parsedPrefs = JSON.parse(userPrefs);
      logger.log('Parsed user preferences:', parsedPrefs);
      
      const quotationStatus = parsedPrefs?.quotationCustomization?.quotationStatus;
      logger.log('Quotation status from preferences:', quotationStatus);
      
      if (!Array.isArray(quotationStatus)) {
        logger.log('No valid quotation status array in preferences');
        return null;
      }
      
      // Log each status found
      quotationStatus.forEach(status => {
        logger.log(`Status found: ${status.value}, Name: ${status.name}, Color: ${status.color}`);
      });
      
      return quotationStatus;
    } catch (error) {
      console.error('Error reading status preferences:', error);
      return null;
    }
  };

  
  const handleConvert = async (targetType: 'order' | 'invoice') => {
    logger.log('Convert button clicked for:', targetType);
    if (!order?._id) {
      logger.log('Convert: No order ID available');
      return;
    }
    
    logger.log(`Convert: Attempting to convert quotation ${order._id} to ${targetType}`);
    
    Modal.confirm({
      title: `Convert to ${targetType}`,
      content: `Are you sure you want to convert this quotation to ${targetType}?`,
      okText: 'Yes, Convert',
      cancelText: 'Cancel',
      onOk: async () => {
        logger.log('Modal confirmed - about to make API call'); // <-- Add this
        setConversionLoading(true);
        try {
          logger.log(`Convert: Starting conversion to ${targetType}`);
          // Inside the onOk function, before the await axios.post:

          // Use the service function instead of direct axios call
          await convertQuotation(order._id, [targetType]);
          logger.log(`Convert: Successfully converted to ${targetType}`);
          message.success(`Quotation converted to ${targetType} successfully`);
          
          // Close the viewer
          onClose();
        } catch (error) {
          console.error('Convert: Error during conversion:', error);
          Logger.log('Error converting quotation:', error);
          message.error(`Failed to convert to ${targetType}`);
        } finally {
          setConversionLoading(false);
          logger.log('Convert: Finished conversion attempt');
        }
      }
    });
    
    // Close the options menu regardless of user's choice
    setOptionsMenuVisible(false);
  };

  // Close the options menu when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        optionsButtonRef.current && 
        !optionsButtonRef.current.contains(event.target as Node) &&
        optionsMenuVisible
      ) {
        setOptionsMenuVisible(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [optionsMenuVisible]);

  const renderOptionsMenu = () => {
    logger.log('OptionsMenu: Rendering menu');
    
    // Get statuses from preferences
    const customStatuses = getStatusPreferences();
    logger.log('Custom statuses from preferences:', customStatuses);
    
    // Use custom statuses if available, otherwise fall back to API statuses
    const availableStatuses = customStatuses || order?.availableStatuses || [
      { value: 'pending', name: 'Pending' },
      { value: 'created', name: 'Created' },
      { value: 'partially_paid', name: 'Partially Paid' },
      { value: 'fully_paid', name: 'Fully Paid' },
      { value: 'overdue', name: 'Overdue' }
    ];
    
    logger.log('Final available statuses for rendering:', availableStatuses);
    logger.log('Current status:', order?.status);
  
    return (
      <OptionsMenu visible={optionsMenuVisible}>
        <OptionsMenuSection>
          <OptionsMenuTitle>Update Status</OptionsMenuTitle>
          {availableStatuses.map(status => {
            const statusValue = status.value;
            const statusName = status.name || status.value.replace('_', ' ').replace(/\b\w/g, (l: string) => l.toUpperCase());
            const statusColor = status.color || generateColorFromString(statusValue);
            
            logger.log(`Rendering status: ${statusValue}, Name: ${statusName}, Color: ${statusColor}`);
            
            return (
              <OptionsMenuItem 
                key={statusValue} 
                onClick={() => {
                  logger.log(`Status ${statusValue} clicked`);
                  handleStatusUpdate(statusValue);
                }}
                disabled={order?.status === statusValue}
                style={{
                  borderLeft: `3px solid ${statusColor}`,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <span 
                    style={{ 
                      width: '10px', 
                      height: '10px', 
                      borderRadius: '50%', 
                      backgroundColor: statusColor,
                      marginRight: '8px'
                    }} 
                  />
                  {statusName}
                </div>
              </OptionsMenuItem>
            );
          })}
        </OptionsMenuSection>
        
        {/* Convert section remains the same */}
        // In the QuotationViewer.tsx, replace the Convert section with:
        // Replace the "Convert To" section in renderOptionsMenu:

<OptionsMenuSection>
  <OptionsMenuTitle>Convert To</OptionsMenuTitle>
  <button
    className="convert-button"
    style={{ 
      width: '100%',
      textAlign: 'left',
      padding: '8px 16px',
      cursor: 'pointer',
      background: 'transparent',
      border: 'none',
      display: 'block'
    }}
    onClick={() => {
      logger.log('Convert to Order button clicked');
      try {
        if (!order?._id) {
          console.error('No order ID available');
          return;
        }
        
        // Close the dropdown first
        setOptionsMenuVisible(false);
        
        // Use a timeout to ensure UI updates before showing modal
        setTimeout(() => {
          logger.log('Showing Order conversion modal');
          Modal.confirm({
            title: `Convert to order`,
            content: `Are you sure you want to convert this quotation to order?`,
            okText: 'Yes, Convert',
            cancelText: 'Cancel',
            onOk: async () => {
              logger.log('Order modal confirmed');
              try {
                await convertQuotation(order._id, ['order']);
                logger.log('Conversion to order succeeded');
                message.success('Quotation converted to order successfully');
                onClose();
              } catch (err) {
                console.error('Order conversion error:', err);
                message.error('Failed to convert to order');
              }
            }
          });
        }, 100);
      } catch (e) {
        console.error('Error in Order conversion handler:', e);
      }
    }}
  >
    Convert to Order
  </button>
  
  <button
    className="convert-button"
    style={{ 
      width: '100%',
      textAlign: 'left',
      padding: '8px 16px',
      cursor: 'pointer',
      background: 'transparent',
      border: 'none',
      display: 'block'
    }}
    onClick={() => {
      logger.log('Convert to Invoice button clicked');
      try {
        if (!order?._id) {
          console.error('No order ID available');
          return;
        }
        
        // Close the dropdown first
        setOptionsMenuVisible(false);
        
        // Use a timeout to ensure UI updates before showing modal
        setTimeout(() => {
          logger.log('Showing Invoice conversion modal');
          Modal.confirm({
            title: `Convert to invoice`,
            content: `Are you sure you want to convert this quotation to invoice?`,
            okText: 'Yes, Convert',
            cancelText: 'Cancel',
            onOk: async () => {
              logger.log('Invoice modal confirmed');
              try {
                await convertQuotation(order._id, ['invoice']);
                logger.log('Conversion to invoice succeeded');
                message.success('Quotation converted to invoice successfully');
                onClose();
              } catch (err) {
                console.error('Invoice conversion error:', err);
                message.error('Failed to convert to invoice');
              }
            }
          });
        }, 100);
      } catch (e) {
        console.error('Error in Invoice conversion handler:', e);
      }
    }}
  >
    Convert to Invoice
  </button>
</OptionsMenuSection>
      </OptionsMenu>
    );
  };
  
  // Helper function to generate a color from a string (copied from statusUtils)
  const generateColorFromString = (str: string): string => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    
    // Generate a color in HSL space for better visual distinction
    // Using fixed saturation and lightness values for consistency
    const hue = Math.abs(hash) % 360;
    return `hsl(${hue}, 70%, 45%)`;
  };

// Replace the entire renderFooter function with this:
const renderFooter = () => {
  // Get custom statuses from the function you already have
  const statusPreferences = getStatusPreferences();
  
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
      {/* Original buttons */}
      <Button onClick={handleCopy}>
        <span style={{ fontSize: '16px', marginRight: '4px' }}>📋</span>
        Copy Link
      </Button>
      <Button primary onClick={handleDownload}>
        <Download size={16} />
        Download
      </Button>
      
      {/* Direct conversion buttons */}
      <Button 
        onClick={() => {
          logger.log('Convert to Order button clicked');
          if (!order?._id) {
            console.error('No order ID available');
            return;
          }
          
          Modal.confirm({
            title: `Convert to order`,
            content: `Are you sure you want to convert this quotation to order?`,
            okText: 'Yes, Convert',
            cancelText: 'Cancel',
            zIndex: 2000, // Add this line to increase z-index
            onOk: async () => {
              logger.log('Order modal confirmed');
              try {
                await convertQuotation(order._id, ['order']);
                logger.log('Conversion to order succeeded');
                message.success('Quotation converted to order successfully');
                onClose();
              } catch (err) {
                console.error('Order conversion error:', err);
                message.error('Failed to convert to order');
              }
            }
          });
        }}
      >
        <Settings size={16} style={{ marginRight: '4px' }} />
        Convert To Order
      </Button>
      
      <Button 
  onClick={() => {
    logger.log('Convert to Invoice button clicked');
    if (!order?._id) {
      console.error('No order ID available');
      return;
    }
    
    Modal.confirm({
      title: `Convert to invoice`,
      content: `Are you sure you want to convert this quotation to invoice?`,
      okText: 'Yes, Convert',
      cancelText: 'Cancel',
      zIndex: 2000, // Higher z-index to appear above the viewer
      maskStyle: { zIndex: 1999 }, // Also set mask z-index
      onOk: async () => {
        logger.log('Invoice modal confirmed');
        try {
          await convertQuotation(order._id, ['invoice']);
          logger.log('Conversion to invoice succeeded');
          message.success('Quotation converted to invoice successfully');
          onClose();
        } catch (err) {
          console.error('Invoice conversion error:', err);
          message.error('Failed to convert to invoice');
        }
      }
    });
  }}
>
  <Settings size={16} style={{ marginRight: '4px' }} />
  Convert To Invoice
</Button>
      
      {/* Status selector as a separate button that shows a modal with status options */}
      {order?.status && (
  <Button
    onClick={() => {
      const availableStatuses = statusPreferences || order?.availableStatuses || [
        { value: 'pending', name: 'Pending' },
        { value: 'created', name: 'Created' },
        { value: 'partially_paid', name: 'Partially Paid' },
        { value: 'fully_paid', name: 'Fully Paid' },
        { value: 'overdue', name: 'Overdue' }
      ];
      
      Modal.info({
        title: 'Update Status',
        content: (
          <div style={{ maxHeight: '300px', overflow: 'auto' }}>
            {availableStatuses.map((status: any) => (
               <div
               key={status.value}
               style={{
                 padding: '12px',
                 margin: '8px 0',
                 cursor: localOrder?.status === status.value ? 'not-allowed' : 'pointer',
                 opacity: localOrder?.status === status.value ? 0.5 : 1,
                 display: 'flex',
                 alignItems: 'center',
                 backgroundColor: localOrder?.status === status.value ? '#f5f5f5' : 'white',
                 border: '1px solid #eee',
                 borderRadius: '4px'
               }}
               onClick={() => {
                 if (localOrder?.status !== status.value) {
                   logger.log(`Status ${status.value} clicked`);
                   handleStatusUpdate(status.value);
                   Modal.destroyAll(); // Close all modals
                 }
               }}
             >
                <span
                  style={{
                    width: '12px',
                    height: '12px',
                    borderRadius: '50%',
                    backgroundColor: status.color || generateColorFromString(status.value),
                    marginRight: '10px'
                  }}
                />
                {status.name || status.value.replace('_', ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}
              </div>
            ))}
          </div>
        ),
        okText: 'Cancel',
        icon: null,
        zIndex: 2000, // Higher z-index to appear above the viewer
        maskStyle: { zIndex: 1999 }, // Also set mask z-index
        onOk: () => {}
      });
    }}
  >
    <span
      style={{
        width: '10px',
        height: '10px',
        borderRadius: '50%',
        backgroundColor: getStatusColor(order.status),
        display: 'inline-block',
        marginRight: '6px'
      }}
    />
    Update Status
  </Button>
)}
    </div>
  );
};

  return (
    <PopupComponent
      isOpen={visible}
      onClose={onClose}
      title="Quotation Details"
      size="lg"
      footer={renderFooter()}
    >
      <ContentWrapper>
        <IframeContainer>
        {!iframeError ? (
          <StyledIframe
            src={getWebViewUrl()}
            onLoad={handleIframeLoad}
            onError={() => setIframeError(true)}
            style={{ overflow: 'hidden' }}
          />
        ) : (
          <ErrorContainer>
            <ErrorMessage>Error loading quotation</ErrorMessage>
            <Button onClick={() => setIframeError(false)}>
              Retry
            </Button>
          </ErrorContainer>
        )}
        
        {loading && (
          <LoadingSpinner>
            <div className="spinner" />
          </LoadingSpinner>
        )}
        </IframeContainer>
      </ContentWrapper>
    </PopupComponent>
  );
};

export default QuotationViewer;